/**
 * IndividualMetricsPanel Component
 *
 * Displays individual performance metrics for an employee:
 * - Evaluations (self, manager, peer average)
 * - OKRs (completion rate, progress)
 * - Feedback (received/given breakdown)
 * - Engagement (1-on-1s, cadence)
 */
import React from 'react'
import { cn } from '@/lib/utils'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card'
import { EngagementIndicator } from './EngagementIndicator'
import type { IndividualMetrics } from '@/types/radar'

interface IndividualMetricsPanelProps {
  metrics: IndividualMetrics
  className?: string
}

export function IndividualMetricsPanel({ metrics, className }: IndividualMetricsPanelProps) {
  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-6', className)}>
      {/* Evaluations Card */}
      <Card>
        <CardHeader>
          <CardTitle>Avaliações</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <EvaluationRow label="Auto-avaliação" score={metrics.evaluations.self} />
            <EvaluationRow label="Gestor" score={metrics.evaluations.manager} />
            <EvaluationRow label="Pares (média)" score={metrics.evaluations.peerAverage} />
            <div className="border-t border-brand-gray/20 pt-3">
              <EvaluationRow label="Geral" score={metrics.evaluations.overall} highlight />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* OKRs Card */}
      <Card>
        <CardHeader>
          <CardTitle>OKRs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-brand-gray">Total</span>
              <span className="text-white font-medium">{metrics.okrs.total}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-brand-gray">Concluídos</span>
              <span className="text-green-500 font-medium">{metrics.okrs.completed}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-brand-gray">Em Progresso</span>
              <span className="text-brand-primary font-medium">{metrics.okrs.inProgress}</span>
            </div>
            <div className="border-t border-brand-gray/20 pt-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-brand-gray">Taxa de Conclusão</span>
                <span className="text-white font-bold">{Math.round(metrics.okrs.completionRate)}%</span>
              </div>
              <div className="h-2 bg-brand-dark-light rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-primary rounded-full transition-all duration-500"
                  style={{ width: `${metrics.okrs.completionRate}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-brand-gray">Progresso Médio</span>
                <span className="text-white">{Math.round(metrics.okrs.avgProgress)}%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback Card */}
      <Card>
        <CardHeader>
          <CardTitle>Feedback</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Received */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-brand-gray">Recebidos</span>
                <span className="text-white font-medium">{metrics.feedback.received.total}</span>
              </div>
              <FeedbackBreakdown breakdown={metrics.feedback.received.byCategory} />
            </div>

            <div className="border-t border-brand-gray/20" />

            {/* Given */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-brand-gray">Dados</span>
                <span className="text-white font-medium">{metrics.feedback.given.total}</span>
              </div>
              <FeedbackBreakdown breakdown={metrics.feedback.given.byCategory} />
            </div>

            <div className="border-t border-brand-gray/20 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-brand-gray">Proporção (dado/recebido)</span>
                <span className={cn(
                  'font-bold',
                  metrics.feedback.ratio >= 1 ? 'text-green-500' : 'text-yellow-500'
                )}>
                  {metrics.feedback.ratio.toFixed(1)}:1
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Engagement Card */}
      <Card>
        <CardHeader>
          <CardTitle>Engajamento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Hidden: 1-on-1s - feature temporarily disabled
            <div>
              <h4 className="text-sm font-medium text-white mb-2">Reuniões 1-on-1</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-brand-gray">Agendadas</span>
                  <span className="text-white">{metrics.engagement.oneOnOnes.scheduled}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-brand-gray">Realizadas</span>
                  <span className="text-white">{metrics.engagement.oneOnOnes.completed}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-brand-gray">Taxa</span>
                  <span className={cn(
                    'font-bold',
                    metrics.engagement.oneOnOnes.rate >= 80 ? 'text-green-500' :
                    metrics.engagement.oneOnOnes.rate >= 50 ? 'text-yellow-500' : 'text-red-500'
                  )}>
                    {Math.round(metrics.engagement.oneOnOnes.rate)}%
                  </span>
                </div>
              </div>
            </div>
            */}

            <div className="flex items-center justify-between">
              <span className="text-sm text-brand-gray">Cadência de Feedback</span>
              <span className={cn(
                'px-2 py-1 rounded text-xs font-medium uppercase',
                metrics.engagement.feedbackCadence === 'high' ? 'bg-green-500/10 text-green-500' :
                metrics.engagement.feedbackCadence === 'medium' ? 'bg-yellow-500/10 text-yellow-500' :
                'bg-red-500/10 text-red-500'
              )}>
                {metrics.engagement.feedbackCadence === 'high' ? 'Alta' :
                 metrics.engagement.feedbackCadence === 'medium' ? 'Média' : 'Baixa'}
              </span>
            </div>

            <div className="border-t border-brand-gray/20 pt-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-brand-gray">Sinal Geral</span>
                <EngagementIndicator signal={metrics.engagement.overallSignal} showLabel />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Evaluation score row with progress bar
 */
function EvaluationRow({
  label,
  score,
  highlight = false,
}: {
  label: string
  score: number | null
  highlight?: boolean
}) {
  const displayScore = score !== null ? score.toFixed(1) : '—'
  const percentage = score !== null ? (score / 5) * 100 : 0

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className={cn(highlight ? 'text-white font-medium' : 'text-brand-gray')}>{label}</span>
        <span className={cn(highlight ? 'text-white font-bold' : 'text-white')}>{displayScore}</span>
      </div>
      <div className="h-2 bg-brand-dark-light rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            score === null ? 'bg-brand-gray/30' :
            score >= 4.5 ? 'bg-green-500' :
            score >= 3.5 ? 'bg-brand-primary' :
            score >= 2.5 ? 'bg-yellow-500' : 'bg-red-500'
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  )
}

/**
 * Feedback category breakdown
 */
function FeedbackBreakdown({ breakdown }: { breakdown: { recognition: number; constructive: number; critical: number } }) {
  return (
    <div className="flex items-center gap-4 text-xs">
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-green-500" />
        <span className="text-brand-gray">{breakdown.recognition} Reconhecimento</span>
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-yellow-500" />
        <span className="text-brand-gray">{breakdown.constructive} Construtivo</span>
      </span>
      <span className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-red-500" />
        <span className="text-brand-gray">{breakdown.critical} Crítico</span>
      </span>
    </div>
  )
}
